import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { TextArea } from '@shared/components/ui/textarea';
import { useNotes } from '../../hooks/useNotes';
import { useToast } from '@shared/hooks/use-toast';

export interface NewNoteModalRef {
  open: (orderId: number) => void;
  openForEdit: (orderId: number, noteId: number, initialContent: string) => void;
}

interface NewNoteModalProps {
  onSuccess?: () => void;
}

/**
 * Modal minimalista para crear una nueva nota.
 * Solo contiene: título "Nota nueva", campo de texto y botón "Añadir nota".
 */
export const NewNoteModal = forwardRef<NewNoteModalRef, NewNoteModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('crmModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [orderId, setOrderId] = useState<number | undefined>(undefined);
  const [content, setContent] = useState('');
  const [editMode, setEditMode] = useState<{ noteId: number } | null>(null);

  const { createNote, updateNote, isCreating, isUpdating } = useNotes(orderId);
  const isSubmitting = isCreating || isUpdating;

  useImperativeHandle(ref, () => ({
    open: (orderIdParam: number) => {
      setOrderId(orderIdParam);
      setContent('');
      setEditMode(null);
      modalRef.current?.openModal();
    },
    openForEdit: (orderIdParam: number, noteId: number, initialContent: string) => {
      setOrderId(orderIdParam);
      setContent(initialContent);
      setEditMode({ noteId });
      modalRef.current?.openModal();
    },
  }));

  const handleSubmit = async () => {
    if (!content.trim() || orderId === undefined) return;

    try {
      if (editMode) {
        await updateNote({
          noteId: editMode.noteId,
          data: { content: content.trim() },
        });
        setContent('');
        setEditMode(null);
        modalRef.current?.closeModal();
        toast({
          title: t('notes.updateSuccess.title', 'Nota actualizada'),
          description: t('notes.updateSuccess.description', 'La nota se ha actualizado correctamente.'),
          variant: 'default',
        });
      } else {
        await createNote({
          content: content.trim(),
          order_id: orderId,
        });
        setContent('');
        modalRef.current?.closeModal();
        toast({
          title: t('notes.createSuccess.title', 'Nota creada'),
          description: t('notes.createSuccess.description', 'La nota se ha creado correctamente.'),
          variant: 'default',
        });
      }
      onSuccess?.();
    } catch (error: any) {
      if (editMode) {
        toast({
          title: t('notes.updateError.title', 'Error al actualizar nota'),
          description:
            error?.response?.data?.message ||
            t('notes.updateError.description', 'Error inesperado al actualizar la nota.'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('notes.createError.title', 'Error al crear nota'),
          description:
            error?.response?.data?.message || t('notes.createError.description', 'Error inesperado al crear la nota.'),
          variant: 'destructive',
        });
      }
    }
  };

  const isEdit = editMode !== null;

  return (
    <ModalCenter
      ref={modalRef}
      title={isEdit ? t('notes.editModal.title', 'Editar nota') : t('notes.newModal.title', 'Nota nueva')}
      type="default"
      width="default"
      className="w-full sm:w-[500px] max-h-[90vh]"
    >
      {/* Campo de texto para la nota - Text field for the note */}
      <TextArea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t('notes.placeholder', 'Comienza a escribir...')}
        rows={8}
        className="resize-none mb-4 min-h-[180px] mt-2"
        data-testid={isEdit ? `edit-note-textarea-${editMode.noteId}` : 'new-note-textarea'}
      />
      {/* Botón Añadir nota / Guardar - Add note / Save button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!content.trim() || isSubmitting}
          data-testid={isEdit ? `save-edit-note-${editMode.noteId}` : 'add-note-button'}
        >
          {isSubmitting
            ? isEdit
              ? t('notes.saving', 'Guardando...')
              : t('notes.creating', 'Añadiendo...')
            : isEdit
              ? t('notes.save', 'Guardar')
              : t('notes.add', 'Añadir nota')}
        </Button>
      </div>
    </ModalCenter>
  );
});

NewNoteModal.displayName = 'NewNoteModal';
